// LotTrackingDetail.js - Complete with Real Backend + Inline Edit Mode + Real PDF Download
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  IoArrowBackCircleSharp,
  IoAddCircleSharp,
  IoCloseCircleSharp,
  IoCreateOutline,
  IoClose
} from "react-icons/io5";
import { MdOutlineDownloadForOffline } from "react-icons/md";
import { FaRegEye, FaSave } from "react-icons/fa";
import { BsArrowRepeat } from "react-icons/bs";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FiSave } from "react-icons/fi";
import Swal from 'sweetalert2';
import html2pdf from 'html2pdf.js';   // ✅ NEW IMPORT
import './LotTracking.css';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

// ============================================================
// AUTH HELPERS
// ============================================================
const getAuthToken = () => {
  const possibleKeys = ['access_token', 'token', 'accessToken', 'authToken', 'jwt'];
  for (let key of possibleKeys) {
    const val = localStorage.getItem(key);
    if (val && val !== 'null' && val !== 'undefined') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') {
          const inner = parsed.access || parsed.token || parsed.access_token;
          if (inner) return inner;
        }
      } catch (e) {
        return val;
      }
      return val;
    }
  }
  return null;
};

const buildHeaders = (withJson = true) => {
  const h = {};
  if (withJson) h['Content-Type'] = 'application/json';
  const token = getAuthToken();
  if (token && token !== 'null' && token !== 'undefined') {
    h['Authorization'] = `Bearer ${token}`;
  }
  return h;
};

const mapBackendTypeToCategory = (backendType) => {
  switch (backendType) {
    case 'image': return 'image';
    case 'cad':
    case 'scan': return 'scan';
    case 'zip': return 'zip';
    default: return 'other';
  }
};

const detectCategoryFromName = (name = '') => {
  const lower = name.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|bmp|webp|jfif)$/.test(lower)) return 'image';
  if (/\.(stl|ply|obj|dcm|slddrw)$/.test(lower) || lower.includes('scan')) return 'scan';
  if (/\.(zip|rar|7z)$/.test(lower)) return 'zip';
  return 'other';
};

// ============================================================
// MAP BACKEND → FRONTEND SHAPE
// ============================================================
const mapManufacturingToDisplay = (raw) => {
  if (!raw) return null;
  return {
    id: raw.id,
    manufacturingDate: raw.manufacturing_date || '',
    lotNumber: raw.lot_number || '',
    partType: raw.part_type || '',
    partName: raw.part_name || '',
    referenceNo: raw.part_reference_number || '',
    operator: raw.machine_operator || '',
    machineInspection: raw.machine_inspection || '',
    quantity: raw.quantity || '',
    partPerWeight: raw.part_per_weight || '',
    totalPartWeight: raw.total_part_weight || '',
    weight: raw.total_part_weight || 0,
    machineNo: raw.machine_number || '',
    materialLotNo: raw.material_lot_number || '',
    internalNumber: raw.internal_lot_number || '',
    partLength: raw.part_length_mm || '',
    partLengthWithWaste: raw.part_length_mm_include_waste || '',
    manufacturingLength: raw.part_manufacturing_with_wastage_length_mm || '',
    manufacturingNos: raw.part_manufacturing_with_wastage_length_nos || '',
    issueBar: raw.issue_bar_for_manufacturing || '',
    returnBalanceBars: raw.return_balance_bar_in_nos || '',
    returnBalanceBarMM: raw.return_balance_bar_in_mm || '',
    perLengthWastage: raw.per_length_mm_wastage_150mm || '',
    wastage: raw.wastage_150mm || '',
    eachPartWaste: raw.each_part_waste || '',
    finalUsedBarNos: raw.final_used_bar_in_nos || '',
    finalUsedBarMM: raw.final_used_bar_in_mm || '',
    city: raw.city || '',
    shop: raw.shop || null,
    shopId: raw.shop?.shop_id || null,
    region: (raw.city || '').toLowerCase().match(
      /karachi|lahore|islamabad|rawalpindi|faisalabad|multan|peshawar|quetta|pakistan|pk/
    ) ? 'pk' : 'uk',
    _raw: raw,
  };
};

const LotTrackingDetail = () => {
  const navigate = useNavigate();
  const { trackingId } = useParams();
  const location = useLocation();
  const passedData = location.state?.data;
  const editModeFromNav = location.state?.editMode || false;

  const [activeTab, setActiveTab] = useState('details');
  const [displayData, setDisplayData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Edit mode state
  const [isEditMode, setIsEditMode] = useState(editModeFromNav);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Files state
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Cities dropdown for edit
  const [cities, setCities] = useState([]);

  // ✅ NEW: PDF download ref & state
  const pdfRef = useRef(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ============================================================
  // FETCH MANUFACTURING RECORD BY ID
  // ============================================================
  useEffect(() => {
    const fetchRecord = async () => {
      if (passedData) {
        const mapped = mapManufacturingToDisplay(passedData._raw || passedData);
        setDisplayData(mapped);
        setEditFormData(mapped);
        setLoading(false);
        return;
      }

      if (!trackingId) {
        setDisplayData(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/`;
        console.log('📥 Fetching record:', url);

        const response = await fetch(url, { headers: buildHeaders(false) });

        if (!response.ok) {
          console.warn('⚠️ Fetch record failed:', response.status);
          setDisplayData(null);
          return;
        }

        const raw = await response.json();
        console.log('✅ Fetched record:', raw);
        const mapped = mapManufacturingToDisplay(raw);
        setDisplayData(mapped);
        setEditFormData(mapped);
      } catch (err) {
        console.error('❌ Error fetching record:', err);
        setDisplayData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [trackingId, passedData]);

  // ============================================================
  // FETCH CITIES (when in edit mode)
  // ============================================================
  useEffect(() => {
    const loadCities = async () => {
      if (!isEditMode) return;
      const shopId = displayData?.shopId;
      if (!shopId) return;
      try {
        const response = await fetch(`${API_BASE_URL}/inventory/shop-cities/?shop_id=${shopId}`);
        if (response.ok) {
          const data = await response.json();
          setCities(data.cities || []);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
      }
    };
    loadCities();
  }, [isEditMode, displayData?.shopId]);

  // ============================================================
  // FETCH FILES
  // ============================================================
  useEffect(() => {
    const fetchFiles = async () => {
      if (!trackingId || trackingId === 'new') return;

      try {
        setFilesLoading(true);
        const url = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/images/all/`;

        const response = await fetch(url, { headers: buildHeaders(false) });

        if (!response.ok) {
          setFiles([]);
          return;
        }

        const data = await response.json();
        const rawList = Array.isArray(data) ? data : (data.files || data.images || []);

        const fetchedFiles = rawList.map(f => {
          const fileUrl = f.file_url || f.image_url || f.url ||
            (typeof f.image === 'string'
              ? (f.image.startsWith('http') ? f.image : `${API_BASE_URL}${f.image.startsWith('/') ? '' : '/'}${f.image}`)
              : null);
          const filename = f.original_filename || f.file_name || f.name ||
            (fileUrl ? fileUrl.split('/').pop() : 'file');
          const category = mapBackendTypeToCategory(f.image_type || f.category || f.file_type);

          return {
            id: f.id,
            name: filename,
            type: f.file_type || '',
            category,
            size: f.file_size || 0,
            date: f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : new Date().toLocaleDateString(),
            updated: f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : new Date().toLocaleDateString(),
            from: f.uploaded_by || 'Admin',
            url: fileUrl,
            preview: category === 'image' ? fileUrl : null,
            isSaved: true,
            file: null,
            isMirror: !!(f.source_lot_tracking_image_id || f.source_lot_tracking_note_id),
          };
        });

        setFiles(fetchedFiles);
      } catch (err) {
        console.error('❌ Error fetching files:', err);
        setFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };

    if (displayData) fetchFiles();
  }, [trackingId, displayData]);

  // ============================================================
  // FETCH NOTES
  // ============================================================
  useEffect(() => {
    const fetchNotes = async () => {
      if (!trackingId || trackingId === 'new') return;

      try {
        setNotesLoading(true);
        const url = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/notes/all/`;

        const response = await fetch(url, { headers: buildHeaders(false) });

        if (!response.ok) {
          setNotes([]);
          return;
        }

        const data = await response.json();
        const rawList = Array.isArray(data) ? data : (data.notes || []);
        const mapped = rawList.map(n => ({
          id: n.id,
          content: n.content,
          created_by: n.created_by || 'Admin',
          created_at: n.created_at,
          isMirror: !!(n.source_lot_tracking_note_id),
        }));

        setNotes(mapped);
      } catch (err) {
        console.error('❌ Error fetching notes:', err);
        setNotes([]);
      } finally {
        setNotesLoading(false);
      }
    };

    if (displayData) fetchNotes();
  }, [trackingId, displayData]);

  // ============================================================
  // ✅ REAL PDF DOWNLOAD (replaces window.print())
  // ============================================================
  const handleDownloadPDF = async () => {
    const element = pdfRef.current;
    if (!element) {
      Swal.fire({
        icon: 'error',
        title: 'Cannot Download',
        text: 'PDF content is not ready yet. Please wait a moment and try again.',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    if (downloadingPdf) return;

    try {
      setDownloadingPdf(true);

      // Build a clean filename
      const lotNo = displayData?.lotNumber || trackingId || 'record';
      const safeLot = String(lotNo).replace(/[^a-z0-9\-_]/gi, '_');
      const fileName = `Manufacturing_Lot_${safeLot}.pdf`;

      const options = {
        margin: [10, 8, 10, 8],          // top, right, bottom, left in mm
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.scrollWidth,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };

      await html2pdf().set(options).from(element).save();

      Swal.fire({
        icon: 'success',
        title: 'PDF Downloaded!',
        text: `${fileName} has been saved to your device.`,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch (err) {
      console.error('❌ PDF download error:', err);
      Swal.fire({
        icon: 'error',
        title: 'PDF Download Failed',
        text: err.message || 'Something went wrong while generating the PDF.',
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ============================================================
  // EDIT HANDLERS
  // ============================================================
  const handleEnterEditMode = () => {
    setEditFormData({ ...displayData });
    setIsEditMode(true);
  };

  const handleCancelEdit = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Cancel Edit?',
      text: 'All unsaved changes will be lost.',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#666',
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'Keep Editing',
    });
    if (result.isConfirmed) {
      setEditFormData({ ...displayData });
      setIsEditMode(false);
      return true;
    }
    return false;
  };

  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  // ============================================================
  // SAVE EDIT → PUT to backend
  // ============================================================
  const handleSaveEdit = async () => {
    if (!editFormData.manufacturingDate || !editFormData.lotNumber || !editFormData.partName) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Required Fields',
        text: 'Manufacturing Date, Lot Number, and Part Name are required.',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    const payload = {
      shop_id: editFormData.shopId || displayData?.shopId,
      city: editFormData.city || null,
      manufacturing_date: editFormData.manufacturingDate,
      lot_number: editFormData.lotNumber,
      part_name: editFormData.partName,
      part_type: editFormData.partType || null,
      part_reference_number: editFormData.referenceNo || null,
      machine_operator: editFormData.operator || null,
      machine_inspection: editFormData.machineInspection || null,
      part_per_weight: editFormData.partPerWeight !== '' ? parseFloat(editFormData.partPerWeight) : null,
      total_part_weight: editFormData.totalPartWeight !== '' ? parseFloat(editFormData.totalPartWeight) : null,
      quantity: editFormData.quantity !== '' ? parseInt(editFormData.quantity) : null,
      machine_number: editFormData.machineNo !== '' ? parseInt(editFormData.machineNo) : null,
      material_lot_number: editFormData.materialLotNo || null,
      internal_lot_number: editFormData.internalNumber || null,
      part_length_mm: editFormData.partLength !== '' ? parseFloat(editFormData.partLength) : null,
      part_length_mm_include_waste: editFormData.partLengthWithWaste !== '' ? parseFloat(editFormData.partLengthWithWaste) : null,
      part_manufacturing_with_wastage_length_mm: editFormData.manufacturingLength !== '' ? parseFloat(editFormData.manufacturingLength) : null,
      part_manufacturing_with_wastage_length_nos: editFormData.manufacturingNos !== '' ? parseFloat(editFormData.manufacturingNos) : null,
      issue_bar_for_manufacturing: editFormData.issueBar !== '' ? parseFloat(editFormData.issueBar) : null,
      return_balance_bar_in_nos: editFormData.returnBalanceBars !== '' ? parseFloat(editFormData.returnBalanceBars) : null,
      return_balance_bar_in_mm: editFormData.returnBalanceBarMM !== '' ? parseFloat(editFormData.returnBalanceBarMM) : null,
      per_length_mm_wastage_150mm: editFormData.perLengthWastage !== '' ? parseFloat(editFormData.perLengthWastage) : null,
      wastage_150mm: editFormData.wastage !== '' ? parseFloat(editFormData.wastage) : null,
      final_used_bar_in_nos: editFormData.finalUsedBarNos !== '' ? parseFloat(editFormData.finalUsedBarNos) : null,
      final_used_bar_in_mm: editFormData.finalUsedBarMM !== '' ? parseFloat(editFormData.finalUsedBarMM) : null,
      each_part_waste: editFormData.eachPartWaste !== '' ? parseFloat(editFormData.eachPartWaste) : null,
    };

    console.log('📤 Updating with payload:', payload);

    try {
      setSavingEdit(true);
      const url = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/update/`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: buildHeaders(true),
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let data;
      try { data = JSON.parse(responseText); } catch { data = { error: responseText }; }

      if (!response.ok) {
        let errorMessage = data.error || data.message || data.detail || 'Failed to update';
        let errorDetails = '';
        const fieldErrors = Object.entries(data)
          .filter(([k, v]) => k !== 'error' && k !== 'message' && k !== 'detail' &&
            (Array.isArray(v) || typeof v === 'string'))
          .map(([field, errs]) => {
            const errList = Array.isArray(errs) ? errs : [errs];
            return `${field}:\n  ${errList.join('\n  ')}`;
          }).join('\n\n');
        if (fieldErrors) errorDetails = fieldErrors;

        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          html: `<div style="text-align:left;">
            <p>${errorMessage}</p>
            ${errorDetails ? `<pre style="background:#f5f5f5;padding:8px;border-radius:6px;font-size:11px;color:#d33;white-space:pre-wrap;">${errorDetails}</pre>` : ''}
          </div>`,
          confirmButtonColor: '#dc2626',
        });
        return;
      }

      const updated = mapManufacturingToDisplay(data);
      setDisplayData(updated);
      setEditFormData(updated);
      setIsEditMode(false);

      Swal.fire({
        icon: 'success',
        title: 'Updated Successfully!',
        text: 'Manufacturing record has been updated.',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('❌ Update error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.message,
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  // ============================================================
  // FILE HANDLERS
  // ============================================================
  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const newFiles = uploadedFiles.map(file => {
      const category = detectCategoryFromName(file.name);
      return {
        id: `local_${Date.now()}_${Math.random()}`,
        name: file.name,
        type: file.type,
        category,
        size: file.size,
        date: new Date().toLocaleDateString(),
        updated: new Date().toLocaleDateString(),
        from: 'Admin',
        file: file,
        preview: file.type.includes('image') ? URL.createObjectURL(file) : null,
        isSaved: false,
      };
    });
    setFiles(prev => [...prev, ...newFiles]);
    Swal.fire({
      icon: 'info', title: 'File Added',
      text: 'Click "Save Files" to upload to server.',
      timer: 1500, showConfirmButton: false, toast: true, position: 'top-end',
    });
  };

  const removeFile = async (fileId) => {
    const fileToRemove = files.find(f => f.id === fileId);
    if (fileToRemove?.isSaved && trackingId && trackingId !== 'new') {
      const result = await Swal.fire({
        icon: 'warning', title: 'Delete File?',
        text: 'This file will be permanently deleted.',
        showCancelButton: true, confirmButtonColor: '#dc2626',
        cancelButtonColor: '#666', confirmButtonText: 'Yes, delete it!',
      });
      if (!result.isConfirmed) return;
      try {
        const url = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/images/${fileId}/`;
        const response = await fetch(url, { method: 'DELETE', headers: buildHeaders(false) });
        if (!response.ok && response.status !== 204) throw new Error(`Failed: ${response.status}`);
        setFiles(prev => prev.filter(f => f.id !== fileId));
        Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Delete Failed', text: err.message, confirmButtonColor: '#dc2626' });
      }
    } else {
      setFiles(prev => prev.filter(f => f.id !== fileId));
    }
  };

  const viewFile = (file) => {
    if (file.isSaved && file.url) {
      if ((file.type && file.type.includes('image')) || file.category === 'image') {
        setSelectedFile(file); setShowPreview(true);
      } else { window.open(file.url, '_blank'); }
    } else if (file.file) {
      if (file.file.type.includes('image')) {
        setSelectedFile(file); setShowPreview(true);
      } else { window.open(URL.createObjectURL(file.file), '_blank'); }
    }
  };

  const downloadFile = (file) => {
    if (file.isSaved && file.url) {
      const a = document.createElement('a');
      a.href = file.url; a.download = file.name; a.target = '_blank';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } else if (file.file) {
      const url = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const replaceFile = (fileId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const newFile = e.target.files[0];
      if (newFile) {
        const category = detectCategoryFromName(newFile.name);
        setFiles(files.map(f => f.id === fileId ? {
          ...f, name: newFile.name, type: newFile.type, category,
          size: newFile.size, updated: new Date().toLocaleDateString(),
          file: newFile,
          preview: newFile.type.includes('image') ? URL.createObjectURL(newFile) : null,
          isSaved: false,
        } : f));
      }
    };
    input.click();
  };

  const getFileIcon = (fileType, fileName) => {
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('zip') || fileName?.includes('.zip')) return '📦';
    if (fileName?.includes('.pdf')) return '📑';
    if (fileName?.includes('.doc') || fileName?.includes('.docx')) return '📝';
    if (fileName?.includes('.xls') || fileName?.includes('.xlsx')) return '📊';
    if (fileName?.toLowerCase().includes('scan')) return '🔍';
    return '📄';
  };

  const closePreview = () => { setShowPreview(false); setSelectedFile(null); };

  const handleSaveFiles = async () => {
    if (!trackingId || trackingId === 'new') {
      Swal.fire({ icon: 'warning', title: 'Save Main Form First', text: 'Please save the record first.', confirmButtonColor: '#004aad' });
      return;
    }
    const unsavedFiles = files.filter(f => !f.isSaved && f.file);
    if (unsavedFiles.length === 0) {
      Swal.fire({ icon: 'info', title: 'No New Files', text: 'All files are already saved.', confirmButtonColor: '#004aad', timer: 1500, showConfirmButton: false });
      return;
    }
    try {
      setSaving(true);
      const uploadUrl = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/images/`;
      let uploadedCount = 0;
      const uploadedServerFiles = [];
      for (const fileObj of unsavedFiles) {
        const formData = new FormData();
        formData.append('image', fileObj.file);
        formData.append('image_type', fileObj.category === 'image' ? 'image' : fileObj.category);
        const headers = {};
        const token = getAuthToken();
        if (token && token !== 'null' && token !== 'undefined') headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(uploadUrl, { method: 'POST', headers, body: formData });
        if (!response.ok) {
          let errMsg = `HTTP ${response.status}`;
          try { const t = await response.text(); errMsg += ` - ${t}`; } catch (_) {}
          throw new Error(`Upload failed for ${fileObj.name}: ${errMsg}`);
        }
        const data = await response.json();
        uploadedCount++;
        uploadedServerFiles.push({ local: fileObj, server: data });
      }
      setFiles(prev => prev.map(f => {
        const match = uploadedServerFiles.find(u => u.local.id === f.id);
        if (match) {
          const serverUrl = match.server.file_url || match.server.image_url || match.server.url;
          return { ...f, isSaved: true, url: serverUrl || f.preview, id: match.server.id || f.id };
        }
        return f;
      }));
      Swal.fire({ icon: 'success', title: 'Files Saved!', text: `${uploadedCount} file(s) uploaded successfully.`, confirmButtonColor: '#004aad', timer: 2000, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Upload Failed', text: err.message, confirmButtonColor: '#dc2626' });
    } finally { setSaving(false); }
  };

  // ============================================================
  // NOTE HANDLERS
  // ============================================================
  const addNote = async () => {
    if (!newNote.trim()) {
      Swal.fire({ icon: 'warning', title: 'Please enter a note', confirmButtonColor: '#004aad' });
      return;
    }
    if (!trackingId || trackingId === 'new') {
      Swal.fire({ icon: 'warning', title: 'Save Main Form First', text: 'Please save the record first.', confirmButtonColor: '#004aad' });
      return;
    }
    try {
      setSavingNote(true);
      const url = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/notes/`;
      const response = await fetch(url, {
        method: 'POST', headers: buildHeaders(true),
        body: JSON.stringify({ content: newNote.trim() }),
      });
      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`;
        try { const t = await response.text(); errMsg += ` - ${t}`; } catch (_) {}
        throw new Error(errMsg);
      }
      const saved = await response.json();
      setNotes(prev => [...prev, {
        id: saved.id, content: saved.content || newNote.trim(),
        created_by: saved.created_by || 'Admin',
        created_at: saved.created_at || new Date().toISOString(),
      }]);
      setNewNote('');
      Swal.fire({ icon: 'success', title: 'Note Added Successfully', confirmButtonColor: '#004aad', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to Add Note', text: err.message, confirmButtonColor: '#dc2626' });
    } finally { setSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete Note?', text: 'This note will be permanently deleted.',
      showCancelButton: true, confirmButtonColor: '#dc2626', cancelButtonColor: '#666',
      confirmButtonText: 'Yes, delete it!', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      const url = `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/notes/${noteId}/delete/`;
      const response = await fetch(url, { method: 'DELETE', headers: buildHeaders(false) });
      if (!response.ok && response.status !== 204) throw new Error(`Failed: ${response.status}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      Swal.fire({ icon: 'success', title: 'Note Deleted', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: err.message, confirmButtonColor: '#dc2626' });
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
    } catch { return isoString; }
  };

  // ============================================================
  // FIELD RENDERER (view/edit toggle)
  // ============================================================
  const renderField = (label, fieldKey, type = 'text', options = null) => {
    const value = editFormData[fieldKey] ?? '';
    if (!isEditMode) {
      const displayVal = displayData[fieldKey];
      return (
        <div className="detail-field-row">
          <label>{label}:</label>
          <span>{displayVal === '' || displayVal == null ? '0' : displayVal}</span>
        </div>
      );
    }
    // Edit mode
    if (options) {
      return (
        <div className="detail-field-row edit-mode">
          <label>{label}:</label>
          <select value={value} onChange={(e) => handleEditFieldChange(fieldKey, e.target.value)}>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt === '' ? '-- Select --' : opt}</option>
            ))}
          </select>
        </div>
      );
    }
    return (
      <div className="detail-field-row edit-mode">
        <label>{label}:</label>
        <input
          type={type}
          value={value}
          onChange={(e) => handleEditFieldChange(fieldKey, e.target.value)}
          step={type === 'number' ? '0.01' : undefined}
        />
      </div>
    );
  };

  // ============================================================
  // LOADING / NO DATA STATES
  // ============================================================
  if (loading) {
    return (
      <div className="lot-tracking-detail-main">
        <div className="detail-header-section">
          <div className="title-button-section">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="lot-tracking-detail-main">
        <div className="detail-header-section">
          <div className="title-button-section">
            <h1>Manufacturing Lot Record</h1>
            <button className="download-pdf-btn" onClick={handleDownloadPDF} disabled={downloadingPdf}>
              {downloadingPdf ? '⏳ Generating...' : '📄 Download PDF'}
            </button>
          </div>
        </div>
        <div className="detail-tab-content">
          <div className="no-data">No data available for ID: {trackingId}</div>
        </div>
      </div>
    );
  }

  const regionName = displayData.region === 'uk' ? 'UK' : 'Pakistan';
  const unsavedCount = files.filter(f => !f.isSaved && f.file).length;

  const imageFiles = files.filter(f => f.category === 'image' || f.type?.includes('image'));
  const scanFiles = files.filter(f => f.category === 'scan' || f.name?.toLowerCase().includes('scan'));
  const zipFiles = files.filter(f => f.category === 'zip' || f.type?.includes('zip') || f.name?.includes('.zip'));
  const otherFiles = files.filter(f =>
    f.category !== 'image' && !f.type?.includes('image') &&
    f.category !== 'scan' && !f.name?.toLowerCase().includes('scan') &&
    f.category !== 'zip' && !f.type?.includes('zip') && !f.name?.includes('.zip')
  );

  const renderFileCard = (file) => (
    <div key={file.id} className={`file-card-vertical ${!file.isSaved ? 'unsaved' : ''}`}>
      <div className="file-thumbnail-vertical" onClick={() => viewFile(file)}>
        {(file.preview || file.url) && (file.type?.includes('image') || file.category === 'image') ? (
          <img src={file.preview || file.url} alt={file.name} className="file-thumbnail-img-vertical" />
        ) : (
          <span className="file-icon-large-vertical">{getFileIcon(file.type, file.name)}</span>
        )}
      </div>
      <div className="file-name-vertical" title={file.name}>{file.name}</div>
      {!file.isSaved && <span className="unsaved-badge">● Unsaved</span>}
      {file.isSaved && file.isMirror && <span className="unsaved-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>🔗 Synced</span>}
      <div className="file-icons-vertical">
        <FaRegEye className="file-action-icon-vertical" onClick={() => viewFile(file)} title="View" />
        <MdOutlineDownloadForOffline className="file-action-icon-vertical" onClick={() => downloadFile(file)} title="Download" />
        <BsArrowRepeat className="file-action-icon-vertical" onClick={() => replaceFile(file.id)} title="Replace" />
        <RiDeleteBin6Fill className="file-action-icon-vertical delete-icon-vertical" onClick={() => removeFile(file.id)} title="Delete" />
      </div>
      <div className="file-meta-vertical">
        <div className="meta-line-vertical">Date: {file.date}</div>
        <div className="meta-line-vertical">Updated: {file.updated}</div>
        <div className="meta-line-vertical">From: {file.from}</div>
      </div>
    </div>
  );

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lot-tracking-detail-main">
      {/* ==================== TOP HEADER ==================== */}
      <div className="detail-header-section">
        <div className="title-button-section">
          <h1>
            Manufacturing Lot Record
          </h1>
          <button
            className="download-pdf-btn"
            onClick={handleDownloadPDF}
            disabled={downloadingPdf}
            style={{ opacity: downloadingPdf ? 0.7 : 1, cursor: downloadingPdf ? 'wait' : 'pointer' }}
          >
            {downloadingPdf ? '⏳ Generating PDF...' : '📄 Download PDF'}
          </button>
        </div>
      </div>

      <div className="detail-container">
        <div className="detail-header">
          <IoArrowBackCircleSharp
            className="back-icon"
            onClick={() => navigate('/lot-tracking')}
            size={32}
            style={{ cursor: 'pointer', color: '#004aad' }}
          />
        </div>

        {/* ✅ PDF CAPTURE WRAPPER START */}
        <div ref={pdfRef} className="pdf-capture-area" style={{ background: '#ffffff', padding: '10px' }}>
          {/* PDF-only header */}
          <div style={{
            display: 'none',
            marginBottom: '12px',
            borderBottom: '2px solid #004aad',
            paddingBottom: '8px',
          }} className="pdf-only-header">
            <h2 style={{ color: '#004aad', margin: 0, fontSize: '18px' }}>
              Manufacturing Lot Record
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#555' }}>
              Lot No: <strong>{displayData.lotNumber || '-'}</strong> &nbsp;|&nbsp;
              Part: <strong>{displayData.partName || '-'}</strong> &nbsp;|&nbsp;
              Date: <strong>{displayData.manufacturingDate || '-'}</strong>
            </p>
          </div>

          {/* ==================== DETAIL BOXES GRID ==================== */}
          <div className="detail-grid-two-column">
            {/* LEFT COLUMN */}
            <div className="left-column">
              <div className="detail-box">
                <div className="detail-box-header"><h3>Basic Information</h3></div>
                <div className="detail-box-content">
                  {renderField('Manufacture Date', 'manufacturingDate', 'date')}
                  {renderField('Lot Number', 'lotNumber')}
                  {renderField('Part Name', 'partName')}
                  {renderField('Part Type', 'partType')}
                  {renderField('Part Reference Number', 'referenceNo')}
                  {isEditMode && (
                    <div className="detail-field-row edit-mode">
                      <label>City:</label>
                      <select
                        value={editFormData.city || ''}
                        onChange={(e) => handleEditFieldChange('city', e.target.value)}
                      >
                        <option value="">-- Select City --</option>
                        {cities.map((c, i) => (
                          <option key={i} value={c}>{c}</option>
                        ))}
                        {editFormData.city && !cities.includes(editFormData.city) && (
                          <option value={editFormData.city}>{editFormData.city}</option>
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-box">
                <div className="detail-box-header"><h3>Machine Users</h3></div>
                <div className="detail-box-content">
                  {renderField('Machine Operator', 'operator')}
                  {renderField('Machine Inspection', 'machineInspection', 'text', ['', 'Passed', 'Failed', 'Pending'])}
                </div>
              </div>

              <div className="detail-box">
                <div className="detail-box-header"><h3>Weight Details</h3></div>
                <div className="detail-box-content">
                  {renderField('Part Per Weight', 'partPerWeight', 'number')}
                  {renderField('Total Part Weight', 'totalPartWeight', 'number')}
                  {renderField('Quantity', 'quantity', 'number')}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="right-column">
              <div className="detail-box">
                <div className="detail-box-header"><h3>Machine Information</h3></div>
                <div className="detail-box-content">
                  {renderField('Machine Number', 'machineNo', 'number')}
                  {renderField('Material Lot Number', 'materialLotNo')}
                  {renderField('Internal Number', 'internalNumber')}
                </div>
              </div>

              <div className="detail-box">
                <div className="detail-box-header"><h3>Dimensions (mm)</h3></div>
                <div className="detail-box-content">
                  {renderField('Part Length (mm)', 'partLength', 'number')}
                  {renderField('Part Length including waste (1.4mm)', 'partLengthWithWaste', 'number')}
                  {renderField('Manufacturing with wastage length (mm)', 'manufacturingLength', 'number')}
                  {renderField("Manufacturing with wastage length (No's)", 'manufacturingNos', 'number')}
                </div>
              </div>

              <div className="detail-box">
                <div className="detail-box-header"><h3>Bar Details</h3></div>
                <div className="detail-box-content">
                  {renderField('Issue bar for manufacturing', 'issueBar', 'number')}
                  {renderField("Return balance bars (No's)", 'returnBalanceBars', 'number')}
                  {renderField('Return balance bar (mm)', 'returnBalanceBarMM', 'number')}
                  {renderField("Final used bar (No's)", 'finalUsedBarNos', 'number')}
                  {renderField('Final used bar (MM)', 'finalUsedBarMM', 'number')}
                  {renderField('Per length mm wastage (150mm)', 'perLengthWastage', 'number')}
                  {renderField('Wasteage (150mm)', 'wastage', 'number')}
                  {renderField('Each part waste', 'eachPartWaste', 'number')}
                </div>
              </div>
            </div>
          </div>

          {/* PDF Footer */}
          <div style={{
            display: 'none',
            marginTop: '16px',
            paddingTop: '8px',
            borderTop: '1px solid #ddd',
            fontSize: '10px',
            color: '#777',
            textAlign: 'center',
          }} className="pdf-only-footer">
            Generated from Hira-Tech Super Admin Panel — {new Date().toLocaleString()}
          </div>
        </div>
        {/* ✅ PDF CAPTURE WRAPPER END */}

        {/* ==================== EDIT MODE BOTTOM ACTION BAR ==================== */}
        {isEditMode && (
          <div className="detail-edit-actions-bar detail-edit-actions-bottom">
            <div className="edit-actions-buttons">
              <button
                type="button"
                className="cancel-edit-btn"
                onClick={handleCancelEdit}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-record-btn"
                onClick={handleSaveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* ==================== VIEW MODE: EDIT BUTTON BELOW BOXES ==================== */}
        {!isEditMode && (
          <div className="detail-view-actions-bar">
            <button
              type="button"
              className="edit-record-btn"
              onClick={handleEnterEditMode}
            >
              <IoCreateOutline size={18} /> Edit Record
            </button>
          </div>
        )}

        {/* ==================== DETAILS & NOTES TABS ==================== */}
        <div className="details-notes-section">
          <div className="details-notes-tabs">
            <button
              className={`details-notes-tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details (Files)
            </button>
            <button
              className={`details-notes-tab ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              Notes ({notes.length})
            </button>
          </div>

          <div className="details-notes-content">
            {/* ---------- FILES TAB ---------- */}
            {activeTab === 'details' && (
              <div className="details-content">
                <div className="details-header">
                  <h4>File Details</h4>
                  <div className="details-header-actions">
                    <label className="add-file-btn">
                      <IoAddCircleSharp size={20} />
                      Add File
                      <input type="file" multiple onChange={handleFileUpload} style={{ display: 'none' }} />
                    </label>
                    {unsavedCount > 0 && (
                      <button type="button" className="save-files-btn" onClick={handleSaveFiles} disabled={saving}>
                        <FiSave size={16} />
                        {saving ? 'Saving...' : `Save Files (${unsavedCount})`}
                      </button>
                    )}
                  </div>
                </div>

                {unsavedCount > 0 && (
                  <div className="unsaved-files-banner">
                    ⚠️ You have {unsavedCount} unsaved file(s). Click <strong>"Save Files"</strong> to upload.
                  </div>
                )}

                {filesLoading ? (
                  <div className="no-files">Loading files...</div>
                ) : (
                  <div className="file-categories">
                    <div className="file-category">
                      <h5>Image Files</h5>
                      <div className="file-list-vertical">
                        {imageFiles.map(renderFileCard)}
                        {imageFiles.length === 0 && (<div className="no-files">No image files uploaded</div>)}
                      </div>
                    </div>
                    <div className="file-category">
                      <h5>Intraoral Scan Files</h5>
                      <div className="file-list-vertical">
                        {scanFiles.map(renderFileCard)}
                        {scanFiles.length === 0 && (<div className="no-files">No intraoral scan files uploaded</div>)}
                      </div>
                    </div>
                    <div className="file-category">
                      <h5>Zip Files</h5>
                      <div className="file-list-vertical">
                        {zipFiles.map(renderFileCard)}
                        {zipFiles.length === 0 && (<div className="no-files">No zip files uploaded</div>)}
                      </div>
                    </div>
                    <div className="file-category">
                      <h5>Other Files</h5>
                      <div className="file-list-vertical">
                        {otherFiles.map(renderFileCard)}
                        {otherFiles.length === 0 && (<div className="no-files">No other files uploaded</div>)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ---------- NOTES TAB ---------- */}
            {activeTab === 'notes' && (
              <div className="notes-content">
                <div className="notes-container">
                  <h3 className="notes-title">Production Notes</h3>
                  <div className="notes-display">
                    {notesLoading ? (
                      <p className="no-notes-message">Loading notes...</p>
                    ) : notes.length === 0 ? (
                      <p className="no-notes-message">No notes available</p>
                    ) : (
                      <div className="notes-list">
                        {notes.map(note => (
                          <div key={note.id} className="note-item">
                            <div className="note-content">{note.content}</div>
                            <div className="note-meta">
                              <span className="note-created-by">{note.created_by}</span>
                              <span className="note-created-at">{formatDate(note.created_at)}</span>
                              {note.isMirror && <span className="note-mirror-badge">🔗 Synced</span>}
                              <button className="note-delete-btn" onClick={() => deleteNote(note.id)} title="Delete Note" type="button">
                                <RiDeleteBin6Fill />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="notes-input-section">
                    <textarea
                      className="notes-textarea-new"
                      placeholder="Add a new note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={4}
                      disabled={savingNote}
                    />
                    <button type="button" className="add-note-btn" onClick={addNote} disabled={savingNote || !trackingId || trackingId === 'new'}>
                      {savingNote ? 'Saving...' : 'Add Note'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ==================== PREVIEW MODAL ==================== */}
        {showPreview && selectedFile && (
          <div className="preview-modal" onClick={closePreview}>
            <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="preview-modal-header">
                <h3>{selectedFile.name}</h3>
                <IoCloseCircleSharp className="close-modal" onClick={closePreview} size={24} />
              </div>
              <div className="preview-modal-body">
                {(selectedFile.preview || selectedFile.url) && (selectedFile.type?.includes('image') || selectedFile.category === 'image') ? (
                  <img src={selectedFile.preview || selectedFile.url} alt={selectedFile.name} className="preview-image" />
                ) : (
                  <div className="preview-file-info">
                    <span className="preview-file-icon">{getFileIcon(selectedFile.type, selectedFile.name)}</span>
                    <p>File: {selectedFile.name}</p>
                    <p>Size: {((selectedFile.size || 0) / 1024).toFixed(2)} KB</p>
                    <p>Type: {selectedFile.type || 'Unknown'}</p>
                    <p>Uploaded: {selectedFile.date}</p>
                    <p>From: {selectedFile.from}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LotTrackingDetail;